import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import Svg, { Path } from 'react-native-svg';
// @ts-expect-error - react-native-web-only API
import { createPortal } from 'react-dom';
import FontFamily from '../../../constants/fonts';

interface StandupListItemProps {
  title: string;
  status?: string;
  statusColor?: string;
  lastModified?: string;
  modifiedBy?: string;
  isSubmitted?: boolean;
  onEditPress?: () => void;
  onViewPress?: () => void;
}

const EditIcon = ({ disabled = false, fillColor }: { disabled?: boolean; fillColor?: string }) => (
  <Svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <Path
      d="M9.21667 5.01667L9.98333 5.78333L2.43333 13.3333H1.66667V12.5667L9.21667 5.01667V5.01667ZM12.2167 0C12.0083 0 11.7917 0.0833333 11.6333 0.241667L10.1083 1.76667L13.2333 4.89167L14.7583 3.36667C15.0833 3.04167 15.0833 2.51667 14.7583 2.19167L12.8083 0.241667C12.6417 0.075 12.4333 0 12.2167 0V0ZM9.21667 2.65833L0 11.875V15H3.125L12.3417 5.78333L9.21667 2.65833V2.65833Z"
      fill={fillColor ?? (disabled ? "#B9C0CA" : "#004895")}
    />
  </Svg>
);

const ViewIcon = ({ disabled = false, fillColor }: { disabled?: boolean; fillColor?: string }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M9.99992 4.99967C13.1583 4.99967 15.9749 6.77467 17.3499 9.58301C15.9749 12.3913 13.1583 14.1663 9.99992 14.1663C6.84159 14.1663 4.02492 12.3913 2.64992 9.58301C4.02492 6.77467 6.84159 4.99967 9.99992 4.99967ZM9.99992 3.33301C5.83325 3.33301 2.27492 5.92467 0.833252 9.58301C2.27492 13.2413 5.83325 15.833 9.99992 15.833C14.1666 15.833 17.7249 13.2413 19.1666 9.58301C17.7249 5.92467 14.1666 3.33301 9.99992 3.33301ZM9.99992 7.49967C11.1499 7.49967 12.0833 8.43301 12.0833 9.58301C12.0833 10.733 11.1499 11.6663 9.99992 11.6663C8.84992 11.6663 7.91659 10.733 7.91659 9.58301C7.91659 8.43301 8.84992 7.49967 9.99992 7.49967ZM9.99992 5.83301C7.93325 5.83301 6.24992 7.51634 6.24992 9.58301C6.24992 11.6497 7.93325 13.333 9.99992 13.333C12.0666 13.333 13.7499 11.6497 13.7499 9.58301C13.7499 7.51634 12.0666 5.83301 9.99992 5.83301Z"
      fill={fillColor ?? (disabled ? "#B9C0CA" : "#004895")}
    />
  </Svg>
);

const StandupListItem: React.FC<StandupListItemProps> = ({
  title,
  status,
  statusColor,
  lastModified,
  modifiedBy,
  onEditPress,
  onViewPress,
  isSubmitted,
}) => {
  const STATUS_COLORS: { [k: string]: string } = {
    'completed': '#027C06',
    'in progress': '#0662C4',
    'saved': '#D6AC16',
    'not started': '#CDCDCD',
    'incomplete': '#C12C01'
  };
  const normalizedStatus = status?.toLowerCase() || '';
  const badgeColor = statusColor || STATUS_COLORS[normalizedStatus] || '#CDCDCD';
  
  // Determine text color based on status - black for Not Started and Saved, white for others
  const getStatusTextColor = () => {
    if (normalizedStatus === 'not started' || normalizedStatus === 'saved') {
      return '#000';
    }
    return '#FFFFFF';
  };
  const isEditable = (status !== "Completed" && status !== "Incomplete" && status==="In Progress" && isSubmitted== false) || status === "Saved";
  const isViewDisabled = status === "Saved" || status==="In Progress" && isSubmitted== false;

  const [isEditTooltipVisible, setIsEditTooltipVisible] = React.useState(false);
  const [isViewTooltipVisible, setIsViewTooltipVisible] = React.useState(false);
  const [isEditHovered, setIsEditHovered] = React.useState(false);
  const [isViewHovered, setIsViewHovered] = React.useState(false);

  const isWeb = Platform.OS === 'web';

  const [editAnchorRect, setEditAnchorRect] = React.useState<DOMRect | null>(null);
  const [viewAnchorRect, setViewAnchorRect] = React.useState<DOMRect | null>(null);

  const formatEmailToName = (email?: string): string => {
    if (!email) {
      return "";
    }
    const namePart = email.split("@")[0];
    const names = namePart.split(".");
    return names
      .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
      .join(" ")
      .replace(/Contractor$/, ""); // Remove "Contractor" suffix if present
  };

  // Ensure button labels have first letter capitalized (safe for any input)
  const capitalizeFirst = (s?: string): string => {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const Tooltip: React.FC<{
    visible: boolean;
    text: string;
    anchorRect: DOMRect | null;
    onRequestClose?: () => void;
    children: React.ReactNode;
  }> = ({ visible, text, anchorRect, onRequestClose, children }) => {
    if (!isWeb) {
      return <>{children}</>;
    }

    const width = 220;
    const bubbleHeight = 34; // approx height of bubble
    const caretHeight = 6;

    const left = anchorRect ? anchorRect.left + anchorRect.width / 2 - width / 2 : 0;
    const top = anchorRect ? anchorRect.top - (bubbleHeight + caretHeight + 8) : 0;

    // Add side-effects to auto-hide tooltip when user moves pointer away, scrolls, resizes, or clicks
    React.useEffect(() => {
      if (!visible) return;

      let autoHideTimer: any = null;

      const onDocMouseMove = (e: MouseEvent) => {
        // If we don't have anchorRect, hide immediately
        if (!anchorRect) {
          onRequestClose?.();
          return;
        }
        const x = e.clientX;
        const y = e.clientY;
        // Add small padding so moving slightly outside doesn't immediately dismiss
        const padding = 8;
        const withinX = x >= (anchorRect.left - padding) && x <= (anchorRect.right + padding);
        const withinY = y >= (anchorRect.top - padding) && y <= (anchorRect.bottom + padding);
        if (!withinX || !withinY) {
          onRequestClose?.();
        }
      };

      const onDocInteraction = () => {
        onRequestClose?.();
      };

      document.addEventListener('mousemove', onDocMouseMove);
      document.addEventListener('scroll', onDocInteraction, true);
      window.addEventListener('resize', onDocInteraction);
      document.addEventListener('mousedown', onDocInteraction);

      // Fallback auto-hide after 5s
      autoHideTimer = setTimeout(() => {
        onRequestClose?.();
      }, 5000);

      return () => {
        clearTimeout(autoHideTimer);
        document.removeEventListener('mousemove', onDocMouseMove);
        document.removeEventListener('scroll', onDocInteraction, true);
        window.removeEventListener('resize', onDocInteraction);
        document.removeEventListener('mousedown', onDocInteraction);
      };
    }, [visible, anchorRect, onRequestClose]);

    const overlay =
      visible && anchorRect ? (
        <View
          style={[
            styles.tooltipFixed,
            {
              left,
              top,
              width,
            },
          ]}
          pointerEvents="none"
        >
          <View style={styles.tooltipBubble}>
            <Text style={styles.tooltipText}>{text}</Text>
          </View>
          <View style={styles.tooltipCaret} />
        </View>
      ) : null;

    return (
      <>
        {children}
        {overlay ? createPortal(overlay as any, document.body) : null}
      </>
    );
  };

  // Ensure hover state clears if pointer leaves the button area or on interaction
  React.useEffect(() => {
    if (!isWeb || !isEditHovered) return;

    // mousemove handler needs MouseEvent for coordinates
    const onMouseMove = (e: MouseEvent) => {
      if (!editAnchorRect) {
        setIsEditHovered(false);
        setIsEditTooltipVisible(false);
        return;
      }
      const x = e.clientX;
      const y = e.clientY;
      const padding = 8;
      const withinX = x >= (editAnchorRect.left - padding) && x <= (editAnchorRect.right + padding);
      const withinY = y >= (editAnchorRect.top - padding) && y <= (editAnchorRect.bottom + padding);
      if (!withinX || !withinY) {
        setIsEditHovered(false);
        setIsEditTooltipVisible(false);
        setEditAnchorRect(null);
      }
    };

    // generic interaction (scroll/resize/mousedown) — uses Event signature
    const onAnyInteraction = (_e?: Event) => {
      setIsEditHovered(false);
      setIsEditTooltipVisible(false);
      setEditAnchorRect(null);
    };

    document.addEventListener('mousemove', onMouseMove as EventListener);
    document.addEventListener('mousedown', onAnyInteraction as EventListener);
    document.addEventListener('scroll', onAnyInteraction as EventListener, true);
    window.addEventListener('resize', onAnyInteraction as EventListener);

    return () => {
      document.removeEventListener('mousemove', onMouseMove as EventListener);
      document.removeEventListener('mousedown', onAnyInteraction as EventListener);
      document.removeEventListener('scroll', onAnyInteraction as EventListener, true);
      window.removeEventListener('resize', onAnyInteraction as EventListener);
    };
  }, [isWeb, isEditHovered, editAnchorRect]);

  React.useEffect(() => {
    if (!isWeb || !isViewHovered) return;

    const onMouseMove = (e: MouseEvent) => {
      if (!viewAnchorRect) {
        setIsViewHovered(false);
        setIsViewTooltipVisible(false);
        return;
      }
      const x = e.clientX;
      const y = e.clientY;
      const padding = 8;
      const withinX = x >= (viewAnchorRect.left - padding) && x <= (viewAnchorRect.right + padding);
      const withinY = y >= (viewAnchorRect.top - padding) && y <= (viewAnchorRect.bottom + padding);
      if (!withinX || !withinY) {
        setIsViewHovered(false);
        setIsViewTooltipVisible(false);
        setViewAnchorRect(null);
      }
    };

    const onAnyInteraction = (_e?: Event) => {
      setIsViewHovered(false);
      setIsViewTooltipVisible(false);
      setViewAnchorRect(null);
    };

    document.addEventListener('mousemove', onMouseMove as EventListener);
    document.addEventListener('mousedown', onAnyInteraction as EventListener);
    document.addEventListener('scroll', onAnyInteraction as EventListener, true);
    window.addEventListener('resize', onAnyInteraction as EventListener);

    return () => {
      document.removeEventListener('mousemove', onMouseMove as EventListener);
      document.removeEventListener('mousedown', onAnyInteraction as EventListener);
      document.removeEventListener('scroll', onAnyInteraction as EventListener, true);
      window.removeEventListener('resize', onAnyInteraction as EventListener);
    };
  }, [isWeb, isViewHovered, viewAnchorRect]);

  return (
    <View style={styles.container}>
      {/* Left Content */}
      <View style={styles.leftContent}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>{title}</Text>
          {status && (
            <View style={[styles.statusBadge, { backgroundColor: badgeColor }]}>
              <Text style={[styles.statusText, { color: getStatusTextColor() }]}>{status}</Text>
            </View>
          )}
        </View>
        {(lastModified || modifiedBy) && (
          <Text style={styles.metadata}>
            Last modified {lastModified || '<timestamp>'} by {formatEmailToName(modifiedBy) || '<name>'}
          </Text>
        )}
      </View>

      {/* Right Actions (grouped buttons with divider) */}
      <View style={styles.actionsContainer}>
        <Tooltip visible={isEditTooltipVisible} text="Edit Stand-up" anchorRect={editAnchorRect} onRequestClose={() => { setIsEditTooltipVisible(false); setEditAnchorRect(null); setIsEditHovered(false); }}>
          <TouchableOpacity
            style={[
              styles.hoverHitArea,
              styles.actionButton,
              // Active = filled appearance; Disabled = white boxed appearance
              isEditable ? styles.filledAction : styles.disabledAction,
              isEditHovered && styles.editHover,
            ]}
             onPress={() => {
                console.log('✏️ [TouchableOpacity] Edit button pressed');
                console.log('onEditPress callback:', typeof onEditPress);
                onEditPress?.();
              }}
            disabled={!isEditable}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityHint="Edit Stand-up"
            {...(isWeb
              ? ({
                  title: 'Edit standup',
                  onMouseEnter: (e: any) => {
                    const rect = e?.currentTarget?.getBoundingClientRect?.();
                    setEditAnchorRect(rect ?? null);
                    setIsEditTooltipVisible(true);
                    setIsEditHovered(true);
                  },
                  onMouseLeave: () => {
                    setIsEditTooltipVisible(false);
                    setEditAnchorRect(null);
                    setIsEditHovered(false);
                  },
                  onMouseDown: (e: any) => {
                    // For web, handle mouseDown to trigger action immediately and prevent the synthetic click
                    if (!isEditable) return;
                    try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
                    onEditPress && onEditPress();
                  },
                } as any)
              : {})}
          >
            <EditIcon disabled={!isEditable} fillColor={isEditHovered ? '#004895' : (isEditable ? '#004895' : '#B1B1B1')} />
            <Text
              style={[
                styles.actionText,
                isEditHovered ? styles.editTextHover : (isEditable ? styles.activeActionText : styles.actionTextDisabled),
              ]}
            >
              {capitalizeFirst('Edit')}
            </Text>
          </TouchableOpacity>
        </Tooltip>

        <View style={styles.actionDivider} />

        <Tooltip visible={isViewTooltipVisible} text="View submitted Stand-up" anchorRect={viewAnchorRect} onRequestClose={() => { setIsViewTooltipVisible(false); setViewAnchorRect(null); setIsViewHovered(false); }}>
          <TouchableOpacity
            style={[
              styles.hoverHitArea,
              styles.actionButton,
              // For view button: not disabled -> active (filled); disabled -> white boxed
              !isViewDisabled ? styles.filledAction : styles.disabledAction,
              isViewHovered && styles.editHover,
            ]}
            onPress={onViewPress}
            disabled={isViewDisabled}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityHint="View submitted Stand-up"
            {...(isWeb
              ? ({
                  title: 'View submitted standup',
                  onMouseEnter: (e: any) => {
                    const rect = e?.currentTarget?.getBoundingClientRect?.();
                    setViewAnchorRect(rect ?? null);
                    setIsViewTooltipVisible(true);
                    setIsViewHovered(true);
                  },
                  onMouseLeave: () => {
                    setIsViewTooltipVisible(false);
                    setViewAnchorRect(null);
                    setIsViewHovered(false);
                  },
                  onMouseDown: (e: any) => {
                    if (isViewDisabled) return;
                    try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
                    onViewPress && onViewPress();
                  },
                } as any)
              : {})}
          >
            <ViewIcon disabled={isViewDisabled} fillColor={isViewHovered ? '#004895' : (!isViewDisabled ? '#004895' : '#B1B1B1')} />
            <Text
              style={[
                styles.actionText,
                isViewHovered ? styles.editTextHover : (!isViewDisabled ? styles.activeActionText : styles.actionTextDisabled),
              ]}
            >
              {capitalizeFirst('View')}
            </Text>
          </TouchableOpacity>
        </Tooltip>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#DADADA',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftContent: {
    flex: 1,
    marginRight: 20,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: 18,
    fontStyle: 'normal',
    fontWeight: '600',
    color: '#1C1C1C',
    lineHeight: 24,
    marginRight: 8,
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  statusBadge: {
    paddingHorizontal: 4,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '400',
    color: '#FFFFFF',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  metadata: Platform.OS === 'web' ? ({
    color: '#000',
    // typography tokens for web with fallbacks
    fontFamily: FontFamily.regular,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 18,
  } as any) : ({
    // Native fallbacks
    color: '#000',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 18,
  } as any),
  actions: {
    flexDirection: 'row',
    gap: 16,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderWidth: 1,
    borderColor: '#D8E7FB',
    borderRadius: 4,
    // Allow tooltip to render outside this container
    overflow: 'visible',
    backgroundColor: '#FFFFFF',
  },
  actionDivider: {
    width: 1,
    backgroundColor: '#E6F0FF',
    alignSelf: 'stretch',
    marginVertical: 0,
  },
  actionButton: Platform.OS === 'web' ? ({
    // Use design tokens (CSS variables) on web with fallbacks
    display: 'flex',
    height: 'var(--Primitives-Spacing-spacing-16, 16px)',
    padding: 'var(--Spacing-spacing-01, 2px) var(--Spacing-spacing-02, 4px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    flexDirection: 'row',
    borderRadius: 4,
    backgroundColor: 'transparent',
  } as any) : ({
    // Native fallback values
    display: 'flex',
    height: 16,
    paddingVertical: 2,
    paddingHorizontal: 4,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    flexDirection: 'row',
    borderRadius: 4,
    backgroundColor: 'transparent',
  } as any),
  actionText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#004895',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  activeActionText: {
    color: '#004895',
  },
  actionTextDisabled: {
    color: '#B1B1B1',
  },
  tooltipWrapper: {
    position: 'relative',
    justifyContent: 'center',
  },
  hoverHitArea: {
    display: 'flex',
    paddingHorizontal: 20,
    paddingVertical: 20,
    borderRadius: 4,
    overflow: 'hidden',
  },
  // Filled (active) action style - white background with colored text/icons
  filledAction: {
    backgroundColor: '#FFFFFF',
  },
  filledActionText: {
    color: '#004895',
  },
  // Disabled action exact boxed look (white card with ash icon/text)
  disabledAction: {
    backgroundColor: '#FFFFFF',
    // remove per-button border so only the outer container border shows
    borderWidth: 0,
  },
  tooltipFixed: {
    position: 'fixed' as any,
    zIndex: 2147483647,
    alignItems: 'center',
  },
  tooltipBubble: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#1C1C1C',
  },
  tooltipCaret: {
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#1C1C1C',
    marginTop: -1,
  },
  tooltipText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
    textAlign: 'center',
  },
  editHover: Platform.OS === 'web' ? ({
    backgroundColor: 'var(--EditHoverBackground, #F5F9FF)'
  } as any) : ({
    backgroundColor: '#F5F9FF'
  } as any),
  editTextHover: {
    color: '#004895',
  },
});

export default StandupListItem;
